import React, { useState, useEffect } from "react";
import { RefreshCw } from "lucide-react";
import { useWidgetTheme } from "@/hooks/useWidgetTheme";
import { useClothFinder } from "@/hooks/useClothFinder";
import { useTryOn } from "@/hooks/useTryOn";
import { useImageUpload } from "@/hooks/useImageUpload";
import { useWidgetState } from "@/hooks/useWidgetState";
import WidgetHeader from "../WidgetHeader/WidgetHeader";
import WidgetInfoTooltip from "../WidgetInfoTooltip/WidgetInfoTooltip";
import WidgetUploadStep from "../WidgetUploadStep/WidgetUploadStep";
import WidgetProcessingStep from "../WidgetProcessingStep/WidgetProcessingStep";
import WidgetResults from "../WidgetResults/WidgetResults";
import WidgetNotAvailable from "../WidgetNotAvailable/WidgetNotAvailable";
import CameraCaptureWithPreview from "@/components/CameraCaptureWithPreview";
import s from "./WidgetContainer.module.css";

const WidgetContainer = ({ apiKey, apiUrl, theme }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [showCameraPreview, setShowCameraPreview] = useState(false);

  useWidgetTheme(theme);

  const { selectedCloth, loading, notFound, setSelectedCloth } = useClothFinder(
    apiKey,
    apiUrl
  );

  const {
    isProcessing,
    resultImages,
    error: tryOnError,
    startTryOn,
    setError: setTryOnError,
  } = useTryOn(apiKey, apiUrl);

  const {
    imageFile: personImage,
    previewUrl,
    error: imageError,
    isLoading: isImageLoading,
    handleFileSelect,
    handleGallerySelect,
    handleRemoveImage,
    setError: setImageError,
  } = useImageUpload();

  const {
    state,
    setUploading,
    setUploaded,
    setProcessing,
    setResult,
    setError: setWidgetError,
    reset,
  } = useWidgetState();

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          userAgent.toLowerCase()
        );
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Синхронизация состояний с загрузкой изображения
  useEffect(() => {
    // Не обновляем состояние, если идет обработка или уже есть результат
    if (state === "processing" || state === "result") {
      return;
    }

    if (isImageLoading) {
      setUploading();
    } else if (personImage && !imageError) {
      // Изображение успешно загружено
      setUploaded();
    } else if (imageError) {
      // Есть ошибка загрузки
      setWidgetError(imageError);
    } else if (!personImage) {
      // Нет изображения - сброс к начальному состоянию
      reset();
    }
  }, [
    isImageLoading,
    personImage,
    imageError,
    state,
    setUploading,
    setUploaded,
    setWidgetError,
    reset,
  ]);

  // Синхронизация состояний с обработкой
  useEffect(() => {
    if (isProcessing && state !== "processing") {
      setProcessing();
    } else if (
      !isProcessing &&
      resultImages.length > 0 &&
      state === "processing"
    ) {
      setResult();
    } else if (tryOnError && state === "processing") {
      setImageError(tryOnError);
      setWidgetError(tryOnError);
      // Возвращаемся к состоянию uploaded при ошибке обработки
      if (personImage) {
        setUploaded();
      }
    }
  }, [
    isProcessing,
    resultImages,
    tryOnError,
    state,
    personImage,
    setProcessing,
    setResult,
    setImageError,
    setWidgetError,
    setUploaded,
  ]);

  const handleStartTryOn = () => {
    if (!personImage) {
      setImageError("Пожалуйста, загрузите ваше фото");
      return;
    }

    if (!selectedCloth) {
      setImageError("Одежда не найдена для этой страницы");
      return;
    }

    setProcessing();
    startTryOn(selectedCloth, personImage);
  };

  const handleTakePhoto = () => {
    setTryOnError(null);
    setImageError(null);
    setShowCameraPreview(true);
  };

  const handleCameraCapture = (file) => {
    handleFileSelect(file);
    setShowCameraPreview(false);
  };

  const handleTryAgain = () => {
    handleRemoveImage();
    reset();
  };

  const handleUploadFromGallery = () => {
    setTryOnError(null);
    setImageError(null);
    handleGallerySelect();
  };

  const handleImagePreviewClick = () => {
    handleGallerySelect();
  };

  const handleDownload = (imageUrl) => {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = `virtual-try-on-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCloseWidget = () => {
    // Отправляем сообщение родительскому окну для закрытия виджета
    if (window.parent !== window) {
      window.parent.postMessage(
        { type: "dressai-widget-close" },
        "*"
      );
    }
  };

  // Определение заголовка в зависимости от состояния
  const getTitle = () => {
    if (state === "result" || state === "processing") {
      return "РЕЗУЛЬТАТ";
    }
    return "Ваше фото";
  };

  // Определение режима подсказки
  const getInfoMode = () => {
    if (state === "processing") {
      return "processing";
    }
    return "upload";
  };

  if (loading) {
    return (
      <div className={s.loading}>
        <div className={s.loadingContent}>
          <RefreshCw className={s.loadingSpinner} />
          <p className={s.loadingText}>Загрузка...</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return <WidgetNotAvailable isMobile={isMobile} onClose={handleCloseWidget} />;
  }

  return (
    <div className={`${s.container} ${s.fadeIn}`}>
      <WidgetHeader
        title={getTitle()}
        onInfoClick={() => setIsInfoOpen(!isInfoOpen)}
        isInfoOpen={isInfoOpen}
      >
        <WidgetInfoTooltip isOpen={isInfoOpen} mode={getInfoMode()} />
      </WidgetHeader>

      {/* Условный рендеринг по состоянию */}
      {state === "idle" ||
      state === "uploading" ||
      state === "uploaded" ||
      state === "error" ? (
        <WidgetUploadStep
          isMobile={isMobile}
          imageFile={personImage}
          previewUrl={previewUrl}
          error={imageError}
          isLoading={isImageLoading}
          onTakePhoto={handleTakePhoto}
          onUploadFromGallery={handleUploadFromGallery}
          onImageClick={handleImagePreviewClick}
          onStartTryOn={handleStartTryOn}
          selectedCloth={selectedCloth}
          isProcessing={isProcessing}
        />
      ) : state === "processing" ? (
        <WidgetProcessingStep isMobile={isMobile} />
      ) : state === "result" ? (
        <WidgetResults
          resultImages={resultImages}
          onDownload={handleDownload}
          onTryAgain={handleTryAgain}
        />
      ) : null}
      {showCameraPreview && (
        <CameraCaptureWithPreview
          onCapture={handleCameraCapture}
          onClose={() => setShowCameraPreview(false)}
        />
      )}
    </div>
  );
};

export default WidgetContainer;
